import React, { useState , useEffect} from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { ArrowLeft, InfoIcon } from 'lucide-react';
import { Footer } from '../../components/Footer/Footer';
import { TokenCard } from '../../components/TokenCard/TokenCard';
import { useNavigate , useParams } from 'react-router-dom';
import { About } from './About';

export function CollectionPage({ appData }) {
  const {getSharablesData} = appData
  const [ aboutExpanded, setAboutExpanded ] = useState(true);
  const [ iconHovered, setIconHovered ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  const [ selectedCollection , setSelectedCollection] = useState(null)
  const navigate = useNavigate();
  const [ nfts , setNfts] = useState(null);
  const { publicId } = useParams();

  useEffect(()=>{
    const getData = async ()=>{
      try {
        setLoading(true)
        if(!publicId){
          throw new Error('Public ID not found')
        }
        const data = await appData.getSharablesData()
        const collectionData =  data.find(val => val.publicId === publicId)
        if(!collectionData){
          throw new Error('Collection not found')
        }
        appData.setSelectedCollection(collectionData)
        setSelectedCollection(collectionData)
        setNfts(collectionData.tokens)
        setLoading(false)
      } catch (error) {
        console.error(error)
        setLoading(false)
        setNfts([])
      }
    }
    getData()
  },[publicId, getSharablesData, appData])

  return (
    <main
      style={{
        minHeight: '100vh',
        background: 'var(--theme-background, #ffffff)',
        color: 'var(--theme-text, #212529)',
        padding: '60px 0 100px',
        position: 'relative',
      }}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        style={{
          position: 'fixed',
          top: 12,
          left: 12,
          background: 'var(--theme-surface, #ffffff)',
          border: '1px solid var(--theme-border, #e9ecef)',
          borderRadius: 12,
          padding: '12px 14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease',
          zIndex: 10,
          minWidth: '44px',
          minHeight: '44px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
        }}
        aria-label="Go back to home"
      >
        <ArrowLeft
          size={20}
          style={{
            color: 'var(--theme-text, #212529)',
          }}
        />
      </button>

      <Container style={{ maxWidth: 1400, padding: '0 16px' }}>
        <header
          style={{
            marginBottom: 24,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div style={{ flex: '0 0 auto', width: '44px' }} />
          <h1
            style={{
              margin: 0,
              fontSize: 'clamp(20px, 5vw, 36px)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--theme-text, #212529)',
              textAlign: 'center',
              flex: '1 1 auto',
              minWidth: 0,
              padding: '0 8px',
            }}
          >
            NFTs on <span style={{ color: 'var(--theme-primary, #007bff)' }}>{selectedCollection?.sharableData?.collectionLabel}</span> collection
          </h1>
          <div
            style={{
              flex: '0 0 auto',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              position: 'relative',
              width: '44px',
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseEnter={() => setIconHovered(true)}
              onMouseLeave={() => setIconHovered(false)}
            >
              <InfoIcon
                size={24}
                style={{
                  color: aboutExpanded ? 'var(--theme-primary, #007bff)' : 'var(--theme-text, #212529)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: iconHovered ? 0.8 : 1,
                  minWidth: '44px',
                  minHeight: '44px',
                  padding: '10px',
                }}
                onClick={() => setAboutExpanded(!aboutExpanded)}
              />
              <div
                style={{
                  position: 'absolute',
                  right: '100%',
                  marginRight: '8px',
                  whiteSpace: 'nowrap',
                  backgroundColor: 'var(--theme-surface, #ffffff)',
                  color: 'var(--theme-text, #212529)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 500,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  border: '1px solid var(--theme-border, #e9ecef)',
                  pointerEvents: 'none',
                  opacity: iconHovered ? 1 : 0,
                  transform: iconHovered ? 'translateX(0)' : 'translateX(-10px)',
                  transition: 'all 0.3s ease',
                  zIndex: 100,
                }}
                className="info-tooltip"
              >
                {aboutExpanded ? 'Hide collection info' : 'Show collection info'}
              </div>
            </div>
          </div>
        </header>

        {/* Expandable About Section */}
        <About
          aboutExpanded={aboutExpanded}
          setAboutExpanded={setAboutExpanded}
          selectedCollection={selectedCollection}
        />

        <section aria-label="NFT collection grid">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
              gap: '16px',
              gridAutoRows: 'auto',
              alignItems: 'start',
            }}
          >
            {nfts && nfts.map((item) => (
              <TokenCard
                key={item.tokenId}
                tokenId={item.tokenId}
                appData={appData}
                collectionData={selectedCollection}
              />
            ))}

            {Array.isArray(nfts) && nfts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <h1 style={{ fontSize: 'clamp(24px, 5vw, 32px)', marginBottom: '20px' }}>No NFTs found</h1>
              </div>
            )}

          </div>
        </section>
        {loading && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}
      </Container>
      <Footer />
    </main>
  );
}

